<template>
  <!-- 관리자가 입금을 확인한 실제 주문. 확인 전 주문은 여기에 오지 않는다. -->
  <section class="slive sui-card sui-card--flush">
    <div class="sui-card__head no-print">
      <h2 class="sui-card__title">
        결제 확인된 주문
        <em>{{ items.length }}건</em>
      </h2>
      <div class="slive__tabs" data-tour="seller-orders-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="slive__tab"
          :class="{ 'is-active': filter === tab.key }"
          @click="setFilter(tab.key)"
        >
          {{ tab.label }}<em v-if="tab.count">{{ tab.count }}</em>
        </button>
      </div>
      <button
        type="button"
        class="sui-btn sui-btn--sm"
        data-tour="seller-orders-export"
        :disabled="!filtered.length"
        @click="downloadExcel"
      >
        <AppIcon icon="mdi-download" :size="15" />
        CSV
      </button>
      <button type="button" class="sui-btn sui-btn--sm" :disabled="loading" @click="load">
        <AppIcon icon="mdi-refresh" :size="15" />
        새로고침
      </button>
    </div>

    <div class="sui-toolbar slive__toolbar no-print" data-tour="seller-orders-toolbar">
      <div class="slive__presets">
        <button
          v-for="item in presets"
          :key="item.key"
          type="button"
          class="sui-btn sui-btn--sm"
          :class="{ 'is-active': preset === item.key }"
          @click="applyPreset(item.key)"
        >
          {{ item.label }}
        </button>
      </div>
      <label class="sui-field slive__date">
        <span>시작</span>
        <input v-model="fromDate" type="date" class="sui-input" @change="onManualDateChange" />
      </label>
      <label class="sui-field slive__date">
        <span>종료</span>
        <input v-model="toDate" type="date" class="sui-input" @change="onManualDateChange" />
      </label>
      <label class="sui-search">
        <AppIcon icon="mdi-magnify" :size="17" />
        <input
          v-model.trim="keyword"
          type="search"
          placeholder="주문번호 · 기관 · 상품 · 수령인"
        />
      </label>
    </div>

    <p class="slive__hint no-print" data-tour="seller-orders-deliver">
      신규 주문은 먼저 「주문 확인」을 눌러 주세요. 확인한 뒤에 발송 처리·송장을 넣을 수 있습니다.
      택배 발송은 송장으로 진행이 자동 조회되고 도착 시 배송완료됩니다.
    </p>
    <aside class="slive__direct-alert no-print" data-tour="seller-orders-direct">
      <AppIcon icon="mdi-alert-circle-outline" :size="18" />
      <div>
        <strong>직접배송은 「배송완료」를 반드시 수동으로 눌러 주세요.</strong>
        <span>
          자동 조회가 없어 도착 후에도 직접 완료 처리해야 정산에 잡힙니다. 배송중 목록에서
          「배송완료」 버튼으로 처리합니다.
        </span>
      </div>
    </aside>

    <p v-if="errorMessage" class="slive__error no-print">
      <AppIcon icon="mdi-alert-outline" :size="16" />
      {{ errorMessage }}
    </p>

    <div v-if="selectedIds.length" class="slive__bulk no-print" data-tour="seller-orders-bulk">
      <span>{{ selectedIds.length }}건 선택</span>
      <button
        type="button"
        class="sui-btn sui-btn--sm"
        :disabled="working || !bulkReadyTargets.length"
        @click="bulkConfirm"
      >
        주문 확인(READY)
      </button>
      <button
        type="button"
        class="sui-btn sui-btn--sm sui-btn--primary"
        :disabled="working || !bulkShipTargets.length"
        @click="openBulkShip"
      >
        일괄 발송
      </button>
      <button
        type="button"
        class="sui-btn sui-btn--sm"
        data-tour="seller-orders-picking"
        :disabled="!selectedItems.length"
        @click="printPickingList"
      >
        피킹리스트 인쇄
      </button>
      <button type="button" class="sui-btn sui-btn--sm sui-btn--ghost" @click="clearSelection">
        선택 해제
      </button>
    </div>

    <div v-if="selectedItems.length" class="slive__picking print-only">
      <h3>피킹리스트</h3>
      <p>{{ todayLabel }} · {{ selectedItems.length }}건</p>
      <table>
        <thead>
          <tr>
            <th>주문번호</th>
            <th>상품</th>
            <th>수량</th>
            <th>수령인</th>
            <th>배송지</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in selectedItems" :key="item.idx">
            <td>{{ item.orderCode }} · {{ mallPayMethodLabel(item) }}</td>
            <td>
              {{ item.name }}
              <template v-if="item.option"> · {{ item.option }}</template>
            </td>
            <td>{{ item.qty }}</td>
            <td>{{ item.receiver }} · {{ item.phone }}</td>
            <td>{{ item.address }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="filtered.length" class="sui-table-wrap no-print" data-tour="seller-orders-table">
      <table class="sui-table">
        <thead>
          <tr>
            <th class="is-center slive__check-col">
              <input
                type="checkbox"
                :checked="allSelectableChecked"
                :indeterminate.prop="someSelectableChecked && !allSelectableChecked"
                aria-label="선택 가능한 항목 전체"
                @change="toggleSelectAll"
              />
            </th>
            <th>주문번호 · 결제확인</th>
            <th>상품</th>
            <th>구매 기관 · 배송지</th>
            <th class="is-num">수량 · 금액</th>
            <th class="is-num">정산 예정</th>
            <th class="is-center">상태</th>
            <th class="is-center">처리</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in filtered"
            :key="item.idx"
            class="slive__row"
            :class="[
              slaClass(item),
              {
                'is-selected': selectedSet.has(item.idx),
                'is-focus': isKeywordFocus(item),
              },
            ]"
          >
            <td class="is-center slive__check-col">
              <input
                v-if="isSelectable(item)"
                type="checkbox"
                :checked="selectedSet.has(item.idx)"
                :aria-label="`${item.orderCode} 선택`"
                @change="toggleSelect(item.idx)"
              />
            </td>
            <td>
              <span class="sui-table__strong">{{ item.orderCode }}</span>
              <span class="slive__pay" :class="{ 'is-card': item.payMethod === 'TOSS' }">{{ mallPayMethodLabel(item) }}</span>
              <span class="sui-table__sub">{{ formatDateTime(item.paidAt) }}</span>
              <span v-if="isKeywordFocus(item)" class="slive__focus-tag">알림에서 연 주문</span>
              <router-link
                v-if="(item.qnaOpenCount || 0) > 0"
                class="slive__qna-chip"
                :to="{
                  path: '/seller/reviews',
                  query: { tab: 'inquiry', filter: 'open', keyword: item.orderCode },
                }"
                :title="`${item.orderCode} 주문의 미답변 상품문의 ${item.qnaOpenCount}건`"
              >
                <AppIcon icon="mdi-comment-question-outline" :size="12" />
                문의 {{ item.qnaOpenCount }}
              </router-link>
            </td>
            <td>
              <span class="sui-prod">
                <img
                  v-if="item.image"
                  class="sui-prod__img"
                  :src="item.image"
                  :alt="item.name"
                  loading="lazy"
                />
                <span>
                  <span class="sui-prod__name">{{ item.name }}</span>
                  <span v-if="item.option" class="sui-table__sub">옵션 · {{ item.option }}</span>
                </span>
              </span>
            </td>
            <td>
              <span class="sui-table__strong">{{ item.buyerOrg }}</span>
              <span class="sui-table__sub">{{ item.receiver }} · {{ item.phone }}</span>
              <span class="slive__addr">{{ item.address }}</span>
              <span v-if="item.memo" class="slive__memo">요청 · {{ item.memo }}</span>
            </td>
            <td class="is-num">
              <span class="sui-table__strong">{{ formatWon(item.amount) }}원</span>
              <span class="sui-table__sub">{{ formatWon(item.price) }}원 × {{ item.qty }}</span>
            </td>
            <td class="is-num">
              <span class="sui-table__strong slive__settle">{{ formatWon(item.settlement) }}원</span>
              <span class="sui-table__sub">내 마진 {{ formatWon(item.sellerMargin) }}원</span>
            </td>
            <td class="is-center">
              <span class="sui-badge" :class="statusTone(item.status)">
                {{ MALL_ORDER_ITEM_STATUS_LABEL[item.status] }}
              </span>
              <span v-if="slaLabel(item)" class="slive__sla" :class="slaClass(item)">
                {{ slaLabel(item) }}
              </span>
              <MallShipmentLedger
                v-if="item.invoice || item.shipments?.length"
                size="compact"
                :item="item"
                @track="openItemShipment(item, $event)"
              />
            </td>
            <td class="is-center" :class="{ 'is-confirm': item.status === 'PAID' }">
              <div class="slive__actions">
                <button
                  v-if="item.status === 'PAID'"
                  type="button"
                  class="sui-btn sui-btn--primary slive__confirm"
                  data-tour="seller-orders-confirm"
                  @click="move(item, 'READY')"
                >
                  주문 확인
                </button>
                <span v-if="item.status === 'PAID'" class="slive__action-hint">
                  확인해야 발송할 수 있습니다
                </span>
                <button
                  v-if="canWriteInvoice(item)"
                  type="button"
                  class="sui-btn sui-btn--sm"
                  :class="{ 'sui-btn--primary': isNewShip(item) }"
                  @click="openShip(item)"
                >
                  {{ shipActionLabel(item) }}
                </button>
                <button
                  v-if="canExtraShip(item)"
                  type="button"
                  class="sui-btn sui-btn--sm"
                  data-tour="seller-orders-extra-ship"
                  title="오발송·누락으로 택배를 한 번 더 보냈을 때. 주문번호는 그대로입니다."
                  @click="openExtraShip(item)"
                >
                  재발송 등록
                </button>
                <button
                  v-if="item.invoice"
                  type="button"
                  class="sui-btn sui-btn--sm"
                  data-tour="seller-orders-tracking"
                  @click="openTracking(item)"
                >
                  배송조회
                </button>
                <button
                  v-if="item.status === 'SHIPPING' && isDirectShip(item)"
                  type="button"
                  class="sui-btn sui-btn--sm sui-btn--primary"
                  @click="move(item, 'DELIVERED')"
                >
                  배송완료
                </button>
                <span v-if="item.status === 'DELIVERED'" class="slive__done">
                  {{ item.deliveredAt ? autoConfirmLabel(item.deliveredAt) : '구매확정 대기' }}
                </span>
                <span v-else-if="item.status === 'DONE'" class="slive__done">
                  처리 완료
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="sui-empty no-print">
      <AppIcon icon="mdi-package-variant-closed" :size="30" />
      <p>
        {{
          loading
            ? '주문을 불러오는 중입니다…'
            : '조건에 맞는 주문이 없습니다. 결제가 확인된 주문만 여기에 표시됩니다.'
        }}
      </p>
    </div>

    <div v-if="shipTarget" class="slive__modal no-print" @click.self="shipTarget = null">
      <div class="slive__dialog">
        <h3>{{ shipDialogTitle(shipTarget) }}</h3>
        <p class="slive__dialog-sub">
          <strong>{{ shipTarget.orderCode }}</strong>
          · {{ shipTarget.buyerOrg }} · {{ shipTarget.name }} · {{ shipTarget.qty }}개
        </p>

        <label class="sui-field">
          <span>택배사</span>
          <select v-model="shipForm.courier" class="sui-select">
            <option v-for="name in COURIERS" :key="name" :value="name">{{ name }}</option>
          </select>
        </label>

        <label class="sui-field">
          <span>송장번호{{ isDirectCourierName(shipForm.courier) ? ' (직접배송은 생략 가능)' : '' }}</span>
          <input v-model.trim="shipForm.invoice" class="sui-input" placeholder="숫자만 입력" />
        </label>

        <p class="slive__dialog-note">
          <template v-if="isDirectCourierName(shipForm.courier)">
            <strong class="slive__dialog-warn">직접배송:</strong>
            발송 후 배송중으로 바뀝니다. 상품이 도착하면 주문 목록에서
            <strong>「배송완료」를 직접 눌러야</strong> 합니다. 자동 완료되지 않습니다.
          </template>
          <template v-else-if="isNewShip(shipTarget)">
            발송하면 배송중으로 바뀝니다. 이후 진행은 송장으로 자동 조회되고, 도착하면 배송완료로
            바뀝니다.
          </template>
          <template v-else>
            송장·택배사만 저장합니다. 주문 상태는 바꾸지 않습니다.
          </template>
        </p>

        <div class="slive__dialog-actions">
          <button type="button" class="sui-btn" @click="shipTarget = null">취소</button>
          <button
            type="button"
            class="sui-btn sui-btn--primary"
            :disabled="working"
            @click="confirmShip"
          >
            {{ isNewShip(shipTarget) ? '발송 처리' : '송장 저장' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="extraShipTarget" class="slive__modal no-print" @click.self="extraShipTarget = null">
      <div class="slive__dialog">
        <h3>재발송 등록</h3>
        <p class="slive__dialog-sub">
          <strong>{{ extraShipTarget.orderCode }}</strong>
          · {{ extraShipTarget.buyerOrg }} · {{ extraShipTarget.name }}
        </p>
        <p class="slive__dialog-note">
          주문번호는 바뀌지 않습니다. 오발송·누락으로 택배를 한 번 더 보냈을 때, 본발송과 다른 송장만
          추가합니다. 카톤 단위는 주문 수량일 뿐 배송을 나누지 않습니다.
        </p>

        <label class="sui-field">
          <span>택배사</span>
          <select v-model="extraShipForm.courier" class="sui-select">
            <option v-for="name in COURIERS" :key="name" :value="name">{{ name }}</option>
          </select>
        </label>

        <label class="sui-field">
          <span>재발송 송장번호{{ isDirectCourierName(extraShipForm.courier) ? ' (직접배송은 생략 가능)' : '' }}</span>
          <input v-model.trim="extraShipForm.invoice" class="sui-input" placeholder="숫자만 입력" />
        </label>

        <label class="sui-field">
          <span>이번에 보내는 수량</span>
          <input
            v-model.number="extraShipForm.qty"
            class="sui-input"
            type="number"
            min="1"
            :max="extraShipTarget.qty"
          />
        </label>

        <label class="sui-field">
          <span>사유</span>
          <input
            v-model.trim="extraShipForm.memo"
            class="sui-input"
            maxlength="120"
            placeholder="예: 오발송 보충"
          />
        </label>

        <div class="slive__dialog-actions">
          <button type="button" class="sui-btn" @click="extraShipTarget = null">취소</button>
          <button
            type="button"
            class="sui-btn sui-btn--primary"
            :disabled="working"
            @click="confirmExtraShip"
          >
            재발송 등록
          </button>
        </div>
      </div>
    </div>

    <div v-if="bulkShipOpen" class="slive__modal no-print" @click.self="bulkShipOpen = false">
      <div class="slive__dialog slive__dialog--wide">
        <h3>일괄 발송</h3>
        <p class="slive__dialog-sub">선택한 {{ bulkShipRows.length }}건에 송장을 입력합니다.</p>
        <p class="slive__dialog-note">
          발송준비 건은 배송중으로 바뀌고, 이미 발송된 건은 송장만 갱신합니다. 직접배송은
          송장 없이 저장할 수 있습니다. 아직 주문 확인 전인 신규는 일괄 발송할 수 없습니다.
        </p>

        <label class="sui-field">
          <span>택배사 (공통)</span>
          <select v-model="bulkCourier" class="sui-select">
            <option v-for="name in COURIERS" :key="name" :value="name">{{ name }}</option>
          </select>
        </label>

        <label class="sui-field">
          <span>송장 붙여넣기 (주문번호 + 송장)</span>
          <textarea
            v-model="bulkPasteText"
            class="sui-input"
            rows="3"
            placeholder="MO-A7X4K2P9&#9;1234567890"
          />
          <button type="button" class="sui-btn sui-btn--sm" @click="applyBulkPaste">
            붙여넣기 적용
          </button>
        </label>

        <div class="slive__bulk-ship-list">
          <div v-for="row in bulkShipRows" :key="row.idx" class="slive__bulk-ship-row">
            <div>
              <strong>{{ row.orderCode }}</strong>
              <span>{{ row.name }} · {{ row.qty }}개</span>
            </div>
            <input
              v-model.trim="bulkInvoices[row.idx]"
              class="sui-input"
              placeholder="송장번호"
            />
          </div>
        </div>

        <div class="slive__dialog-actions">
          <button type="button" class="sui-btn" @click="bulkShipOpen = false">취소</button>
          <button
            type="button"
            class="sui-btn sui-btn--primary"
            :disabled="working"
            @click="confirmBulkShip"
          >
            일괄 발송 처리
          </button>
        </div>
      </div>
    </div>

    <MallTrackingModal
      v-if="trackTarget"
      :item-idx="trackTarget.idx"
      :courier="trackTarget.courier"
      :invoice="trackTarget.invoice"
      :product-name="trackTarget.name"
      :order-code="trackTarget.orderCode"
      @close="onCloseTracking"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import MallShipmentLedger from '@/components/mall/MallShipmentLedger.vue'
import MallTrackingModal from '@/components/mall/MallTrackingModal.vue'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { useListDateRange } from '@/composables/useListDateRange'
import { useSellerData } from '@/composables/useSellerData'
import { MALL_COURIERS as COURIERS, isKnownMallCourier } from '@/constants/mallCouriers'
import { formatWon } from '@/constants/sellerData'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  fetchSellerOrders,
  updateSellerOrderStatus,
  createSellerExtraShipment,
  type ItemStatusCounts,
  type MallOrderItemStatus,
  mallPayMethodLabel,
  type SellerOrderItem,
} from '@/api/mallOrder'
import { csvEscape } from '@/utils/csv'
import { isExistingWaybill, type ShipmentLedgerRow } from '@/utils/mallShipment'
import { autoConfirmLabel } from '@/utils/mallOpsUx'

const LAST_COURIER_KEY = 'hamuri.seller.lastCourier'

function loadLastCourier(): string {
  try {
    const raw = localStorage.getItem(LAST_COURIER_KEY)
    if (raw && isKnownMallCourier(raw)) return raw
  } catch {
    /* ignore */
  }
  return COURIERS[0]
}

function saveLastCourier(name: string) {
  try {
    localStorage.setItem(LAST_COURIER_KEY, name)
  } catch {
    /* ignore */
  }
}

const STATUS_TONE: Record<MallOrderItemStatus, string> = {
  WAITING: 'sui-badge--gray',
  PAID: 'sui-badge--blue',
  READY: 'sui-badge--violet',
  SHIPPING: 'sui-badge--violet',
  DELIVERED: 'sui-badge--green',
  DONE: 'sui-badge--green',
  CANCEL_REQUEST: 'sui-badge--amber',
  RETURN_REQUEST: 'sui-badge--amber',
  CANCELED: 'sui-badge--red',
}

const { notify } = useSellerData()
const route = useRoute()
const router = useRouter()
const { fromDate, toDate, preset, presets, applyPreset, onManualDateChange, inRange } =
  useListDateRange('3m')

/*
 * 알림·리뷰·문의 화면에서 주문번호로 넘어오면(예: /seller/orders?keyword=MO-XXXX) 기본 3개월
 * 필터에 걸려 화면이 비어 보이는 것을 막는다.
 */
if (String(route.query.keyword ?? '').trim()) {
  applyPreset('all')
}

const items = ref<SellerOrderItem[]>([])
const counts = ref<ItemStatusCounts>({})
const loading = ref(false)
const errorMessage = ref('')
const working = ref(false)
const trackTarget = ref<{
  idx: number
  courier?: string
  invoice?: string
  name?: string
  orderCode?: string
} | null>(null)
const filter = ref<'all' | MallOrderItemStatus>('all')

function openTracking(item: SellerOrderItem) {
  trackTarget.value = item
}

function openItemShipment(item: SellerOrderItem, row: ShipmentLedgerRow) {
  trackTarget.value = {
    idx: item.idx,
    courier: row.courier,
    invoice: row.invoice,
    name: item.name,
    orderCode: item.orderCode,
  }
}

function isDirectCourierName(courier: string) {
  return /직접/.test(courier || '')
}

function isDirectShip(item: SellerOrderItem) {
  return isDirectCourierName(item.courier || '')
}

/** 주문 확인(READY) 이후에만 송장·발송. 신규(PAID)는 확인부터 한다. */
function canWriteInvoice(item: SellerOrderItem) {
  return (
    item.status === 'READY' ||
    item.status === 'SHIPPING' ||
    item.status === 'DELIVERED' ||
    item.status === 'DONE'
  )
}

function isNewShip(item: SellerOrderItem) {
  return item.status === 'READY'
}

function shipActionLabel(item: SellerOrderItem) {
  if (isNewShip(item)) return '발송 처리'
  if (item.invoice) return '수정'
  return '송장 입력'
}

function shipDialogTitle(item: SellerOrderItem | null) {
  if (!item) return '송장 입력'
  if (isNewShip(item)) return '발송 처리'
  if (item.invoice) return '송장 수정'
  return '송장 입력'
}

function canExtraShip(item: SellerOrderItem) {
  return item.status === 'SHIPPING' || item.status === 'DELIVERED' || item.status === 'DONE'
}

function isKeywordFocus(item: SellerOrderItem) {
  const query = keyword.value.trim().toLowerCase()
  if (!query) return false
  return item.orderCode.toLowerCase() === query
}

function onCloseTracking() {
  trackTarget.value = null
  void load()
}
const selectedIds = ref<number[]>([])
const keyword = ref(String(route.query.keyword ?? '').trim())

const shipTarget = ref<SellerOrderItem | null>(null)
const shipForm = reactive({ courier: loadLastCourier(), invoice: '' })

const extraShipTarget = ref<SellerOrderItem | null>(null)
const extraShipForm = reactive({
  courier: loadLastCourier(),
  invoice: '',
  qty: 1,
  memo: '오발송·누락 재발송',
})

const bulkShipOpen = ref(false)
const bulkCourier = ref(loadLastCourier())
const bulkInvoices = reactive<Record<number, string>>({})
const bulkShipRows = ref<SellerOrderItem[]>([])
const bulkPasteText = ref('')

const FILTER_KEYS = new Set<MallOrderItemStatus>([
  'PAID',
  'READY',
  'SHIPPING',
  'DELIVERED',
  'DONE',
  'CANCELED',
  'CANCEL_REQUEST',
])

const todayLabel = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}).format(new Date())

function parseStatusQuery(raw: unknown): 'all' | MallOrderItemStatus {
  const value = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (!value || value === 'ALL') return 'all'
  if (FILTER_KEYS.has(value as MallOrderItemStatus)) {
    return value as MallOrderItemStatus
  }
  return 'all'
}

function applyStatusQuery() {
  filter.value = parseStatusQuery(route.query.status)
}

async function load() {
  loading.value = true
  errorMessage.value = ''

  try {
    const result = await fetchSellerOrders()
    items.value = result.items
    counts.value = result.counts
    const alive = new Set(result.items.map((item) => item.idx))
    selectedIds.value = selectedIds.value.filter((idx) => alive.has(idx))
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '주문을 불러오지 못했습니다.'
    items.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  applyStatusQuery()
  applyKeywordQuery()
  load()
})

function applyKeywordQuery() {
  keyword.value = String(route.query.keyword ?? '').trim()
}

watch(
  () => route.query.status,
  () => {
    applyStatusQuery()
    clearSelection()
  },
)

watch(
  () => route.query.keyword,
  () => {
    applyKeywordQuery()
  },
)

watch(keyword, (value) => {
  const next = String(value || '').trim()
  const current = String(route.query.keyword ?? '').trim()
  if (next === current) return
  const query = { ...route.query }
  if (next) query.keyword = next
  else delete query.keyword
  router.replace({ query })
})

const tabs = computed(() => [
  { key: 'all' as const, label: '전체', count: items.value.length },
  { key: 'PAID' as const, label: '신규', count: counts.value.PAID ?? 0 },
  { key: 'READY' as const, label: '발송준비', count: counts.value.READY ?? 0 },
  { key: 'SHIPPING' as const, label: '배송중', count: counts.value.SHIPPING ?? 0 },
  { key: 'DELIVERED' as const, label: '배송완료', count: counts.value.DELIVERED ?? 0 },
  {
    key: 'CANCELED' as const,
    label: '취소',
    count: (counts.value.CANCELED ?? 0) + (counts.value.CANCEL_REQUEST ?? 0),
  },
])

const filtered = computed(() => {
  const query = keyword.value.toLowerCase()
  return items.value.filter((item) => {
    if (filter.value === 'CANCELED') {
      if (item.status !== 'CANCELED' && item.status !== 'CANCEL_REQUEST') return false
    } else if (filter.value !== 'all' && item.status !== filter.value) {
      return false
    }
    if (!inRange(item.paidAt || item.orderedAt)) return false
    if (!query) return true
    return (
      item.orderCode.toLowerCase().includes(query) ||
      item.buyerOrg.toLowerCase().includes(query) ||
      item.name.toLowerCase().includes(query) ||
      item.receiver.toLowerCase().includes(query) ||
      (item.poNumber || '').toLowerCase().includes(query)
    )
  })
})

const selectedSet = computed(() => new Set(selectedIds.value))

function isSelectable(item: SellerOrderItem) {
  return item.status === 'PAID' || canWriteInvoice(item)
}

const selectableFiltered = computed(() => filtered.value.filter(isSelectable))

const allSelectableChecked = computed(
  () =>
    selectableFiltered.value.length > 0 &&
    selectableFiltered.value.every((item) => selectedSet.value.has(item.idx)),
)

const someSelectableChecked = computed(() =>
  selectableFiltered.value.some((item) => selectedSet.value.has(item.idx)),
)

const selectedItems = computed(() =>
  items.value.filter((item) => selectedSet.value.has(item.idx)),
)

const bulkReadyTargets = computed(() =>
  selectedItems.value.filter((item) => item.status === 'PAID'),
)

const bulkShipTargets = computed(() =>
  selectedItems.value.filter((item) => canWriteInvoice(item)),
)

function setFilter(next: 'all' | MallOrderItemStatus) {
  filter.value = next
  const query = { ...route.query }
  if (next === 'all') {
    delete query.status
  } else {
    query.status = next.toLowerCase()
  }
  router.replace({ path: route.path, query })
}

function statusTone(status: MallOrderItemStatus): string {
  return STATUS_TONE[status] ?? 'sui-badge--gray'
}

function toggleSelect(idx: number) {
  if (selectedSet.value.has(idx)) {
    selectedIds.value = selectedIds.value.filter((id) => id !== idx)
  } else {
    selectedIds.value = [...selectedIds.value, idx]
  }
}

function toggleSelectAll(event: Event) {
  const checked = (event.target as HTMLInputElement).checked
  const ids = selectableFiltered.value.map((item) => item.idx)
  if (checked) {
    selectedIds.value = [...new Set([...selectedIds.value, ...ids])]
  } else {
    const drop = new Set(ids)
    selectedIds.value = selectedIds.value.filter((id) => !drop.has(id))
  }
}

function clearSelection() {
  selectedIds.value = []
}

async function bulkConfirm() {
  if (working.value || !bulkReadyTargets.value.length) return
  const ok = await confirmDialog({
    title: '일괄 주문 확인',
    message: `${bulkReadyTargets.value.length}건을 발송준비로 바꿀까요?`,
    confirmLabel: '확인',
    tone: 'primary',
  })
  if (!ok) return

  working.value = true
  try {
    for (const item of bulkReadyTargets.value) {
      await updateSellerOrderStatus(item.idx, 'READY')
    }
    notify(`${bulkReadyTargets.value.length}건을 발송준비로 바꿨습니다.`)
    clearSelection()
    await load()
  } catch (error) {
    notify(error instanceof Error ? error.message : '일괄 주문 확인에 실패했습니다.')
  } finally {
    working.value = false
  }
}

function openBulkShip() {
  const rows = bulkShipTargets.value
  if (!rows.length) return
  bulkShipRows.value = rows
  bulkCourier.value = loadLastCourier()
  bulkPasteText.value = ''
  rows.forEach((row) => {
    bulkInvoices[row.idx] = row.invoice || ''
  })
  bulkShipOpen.value = true
}

function applyBulkPaste() {
  const lines = bulkPasteText.value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
  if (!lines.length) {
    notify('붙여넣을 내용을 입력해 주세요.')
    return
  }
  let applied = 0
  for (const line of lines) {
    const parts = line
      .split(/[\t,]/)
      .map((part) => part.trim())
      .filter(Boolean)
    if (parts.length < 2) continue
    const orderCode = parts[0]
    const invoice = parts.slice(1).join('').trim()
    if (!orderCode || !invoice) continue
    const row = bulkShipRows.value.find((item) => item.orderCode === orderCode)
    if (!row) continue
    bulkInvoices[row.idx] = invoice
    applied += 1
  }
  if (!applied) {
    notify('일치하는 주문번호가 없습니다.')
    return
  }
  notify(`${applied}건 송장번호를 채웠습니다.`)
}

function printPickingList() {
  if (!selectedItems.value.length) return
  window.print()
}

async function confirmBulkShip() {
  if (working.value || !bulkShipRows.value.length) return

  const courier = bulkCourier.value
  const direct = isDirectCourierName(courier)
  if (!direct) {
    const missing = bulkShipRows.value.find((row) => !String(bulkInvoices[row.idx] || '').trim())
    if (missing) {
      notify('모든 행에 송장번호를 입력해 주세요. 직접배송은 생략할 수 있습니다.')
      return
    }
  }

  working.value = true
  try {
    for (const row of bulkShipRows.value) {
      const nextStatus = isNewShip(row) ? 'SHIPPING' : row.status
      await updateSellerOrderStatus(row.idx, nextStatus, {
        courier,
        invoice: String(bulkInvoices[row.idx] || '').trim(),
      })
    }
    saveLastCourier(courier)
    notify(`${bulkShipRows.value.length}건 송장을 저장했습니다.`)
    bulkShipOpen.value = false
    clearSelection()
    await load()
  } catch (error) {
    notify(error instanceof Error ? error.message : '일괄 발송에 실패했습니다.')
  } finally {
    working.value = false
  }
}

async function move(item: SellerOrderItem, status: MallOrderItemStatus) {
  const delivering = status === 'DELIVERED'
  const ok = await confirmDialog({
    title: delivering ? '배송완료' : '주문 상태 변경',
    message: delivering
      ? `${item.orderCode} 상품이 도착했습니까? 도착한 뒤에만 배송완료로 바꿔 주세요.`
      : `${item.orderCode} 주문을 '${MALL_ORDER_ITEM_STATUS_LABEL[status]}'으로 바꿀까요?`,
    detail: delivering
      ? '배송완료 후 7일이 지나면 구매가 자동 확정되고, 구매확정된 달 정산에 잡힙니다.'
      : undefined,
    confirmLabel: delivering ? '배송완료' : '변경',
    tone: 'primary',
  })
  if (!ok) return

  try {
    await updateSellerOrderStatus(item.idx, status)
    notify(
      delivering
        ? '배송완료로 바꿨습니다. 구매 기관이 확정하거나 7일이 지나면 정산에 반영됩니다.'
        : `'${MALL_ORDER_ITEM_STATUS_LABEL[status]}'으로 바꿨습니다.`,
    )
    await load()
  } catch (error) {
    notify(error instanceof Error ? error.message : '주문 상태를 바꾸지 못했습니다.')
  }
}

function openShip(item: SellerOrderItem) {
  shipTarget.value = item
  shipForm.courier = item.courier || loadLastCourier()
  shipForm.invoice = item.invoice || ''
}

function openExtraShip(item: SellerOrderItem) {
  extraShipTarget.value = item
  extraShipForm.courier = loadLastCourier()
  extraShipForm.invoice = ''
  extraShipForm.qty = Math.max(1, Math.min(item.qty, 1))
  extraShipForm.memo = '오발송·누락 재발송'
}

async function confirmExtraShip() {
  if (!extraShipTarget.value || working.value) return

  const courier = extraShipForm.courier
  const invoice = extraShipForm.invoice.trim()
  const direct = isDirectCourierName(courier)
  const qty = Math.max(1, Math.trunc(Number(extraShipForm.qty) || 1))
  const memo = extraShipForm.memo.trim() || '오발송·누락 재발송'

  if (!direct && !invoice) {
    notify('재발송 송장번호를 입력해 주세요. 직접배송은 생략할 수 있습니다.')
    return
  }
  if (!direct && isExistingWaybill(extraShipTarget.value, invoice)) {
    notify('이미 있는 송장입니다. 택배를 따로 보냈을 때만 다른 송장을 넣어 주세요.')
    return
  }
  if (qty > extraShipTarget.value.qty) {
    notify(`재발송 수량은 주문 수량(${extraShipTarget.value.qty})을 넘을 수 없습니다.`)
    return
  }

  const ok = await confirmDialog({
    title: '재발송 등록',
    message: `${extraShipTarget.value.orderCode} 주문에 재발송 송장을 추가할까요? 주문번호는 그대로입니다.`,
    detail: `${extraShipTarget.value.buyerOrg}\n${extraShipTarget.value.name}\n${courier}${
      invoice ? `\n송장 ${invoice}` : '\n송장 없음(직접배송)'
    }\n수량 ${qty}\n사유 ${memo}`,
    confirmLabel: '등록',
    tone: 'primary',
  })
  if (!ok) return

  working.value = true
  try {
    await createSellerExtraShipment(extraShipTarget.value.idx, {
      courier,
      invoice,
      qty,
      memo,
    })
    saveLastCourier(courier)
    notify('재발송 송장을 등록했습니다. 주문번호는 그대로입니다.')
    extraShipTarget.value = null
    await load()
  } catch (error) {
    notify(error instanceof Error ? error.message : '재발송 등록에 실패했습니다.')
  } finally {
    working.value = false
  }
}

async function confirmShip() {
  if (!shipTarget.value || working.value) return

  const courier = shipForm.courier
  const invoice = shipForm.invoice.trim()
  const direct = isDirectCourierName(courier)
  const shippingNew = isNewShip(shipTarget.value)

  if (!direct && !invoice) {
    notify('송장번호를 입력해 주세요. 직접배송은 생략할 수 있습니다.')
    return
  }

  const ok = await confirmDialog({
    title: shippingNew ? '발송 처리' : '송장 저장',
    message: shippingNew
      ? `${shipTarget.value.orderCode} 주문을 발송 처리할까요?`
      : `${shipTarget.value.orderCode} 송장을 저장할까요?`,
    detail: `${courier}${invoice ? `\n송장 ${invoice}` : '\n송장 없음(직접배송)'}${
      shippingNew ? '\n이후 진행은 송장으로 자동 조회됩니다.' : '\n주문 상태는 유지됩니다.'
    }`,
    confirmLabel: shippingNew ? '발송' : '저장',
    tone: 'primary',
  })
  if (!ok) return

  working.value = true

  try {
    const nextStatus = shippingNew ? 'SHIPPING' : shipTarget.value.status
    await updateSellerOrderStatus(shipTarget.value.idx, nextStatus, {
      courier,
      invoice,
    })
    saveLastCourier(courier)
    notify(
      shippingNew
        ? '발송 처리했습니다. 이후 진행은 송장으로 자동 조회됩니다.'
        : '송장을 저장했습니다.',
    )
    shipTarget.value = null
    await load()
  } catch (error) {
    notify(error instanceof Error ? error.message : '발송 처리에 실패했습니다.')
  } finally {
    working.value = false
  }
}

function orderAgeHours(item: SellerOrderItem): number | null {
  if (item.status !== 'PAID' && item.status !== 'READY') return null
  const raw = item.paidAt || item.orderedAt
  if (!raw) return null
  const at = new Date(raw).getTime()
  if (Number.isNaN(at)) return null
  return Math.max(0, (Date.now() - at) / 36e5)
}

function slaClass(item: SellerOrderItem): string {
  const hours = orderAgeHours(item)
  if (hours == null) return ''
  if (hours >= 48) return 'is-sla-alert'
  if (hours >= 24) return 'is-sla-warn'
  return ''
}

function slaLabel(item: SellerOrderItem): string {
  const hours = orderAgeHours(item)
  if (hours == null) return ''
  if (hours >= 48) return '발송 지연 48h+'
  if (hours >= 24) return '발송 주의 24h+'
  return ''
}

function formatDateTime(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')} ${String(
    date.getHours(),
  ).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function downloadExcel() {
  const rows = filtered.value
  if (!rows.length) return

  const header = [
    '주문번호',
    '결제수단',
    '결제확인일시',
    '상품명',
    '옵션',
    '수량',
    '금액',
    '정산예정',
    '구매기관',
    '수령인',
    '연락처',
    '배송지',
    '발주번호',
    '상태',
    '택배사',
    '송장번호',
  ]
  const body = rows.map((item) =>
    [
      item.orderCode,
      mallPayMethodLabel(item),
      formatDateTime(item.paidAt),
      item.name || '',
      item.option || '',
      String(item.qty ?? ''),
      String(item.amount ?? ''),
      String(item.settlement ?? ''),
      item.buyerOrg || '',
      item.receiver || '',
      item.phone || '',
      item.address || '',
      item.poNumber || '',
      MALL_ORDER_ITEM_STATUS_LABEL[item.status] || item.status,
      item.courier || '',
      item.invoice || '',
    ]
      .map((cell) => csvEscape(cell))
      .join(','),
  )
  const csv = `\uFEFF${[header.join(','), ...body].join('\n')}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `판매주문_${new Date().toISOString().slice(0, 10)}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
}
</script>

<style lang="scss" src="./SellerLiveOrders.scss"></style>
